/**
 * TaxSavingsBreakdown - Premium Tax Savings Display
 *
 * Premium fintech aesthetic - clean, minimal, monochrome with subtle accents.
 * Shows tax savings breakdown with professional styling.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { TaxSavingsHighlight } from '../../../api/_schemas/journeyOutput.schema';

interface TaxSavingsBreakdownProps {
  savings: TaxSavingsHighlight;
  className?: string;
}

// Category icons (SVG) - professional, monochrome
const CategoryIcons = {
  foreign: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  gains: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  treaty: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  retirement: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  property: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  dividend: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  default: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
};

// Get icon for category
function getCategoryIcon(category: string) {
  const lower = category.toLowerCase();
  if (lower.includes('rnor') || lower.includes('foreign')) return CategoryIcons.foreign;
  if (lower.includes('capital') || lower.includes('gains')) return CategoryIcons.gains;
  if (lower.includes('dtaa') || lower.includes('treaty')) return CategoryIcons.treaty;
  if (lower.includes('retirement') || lower.includes('401k') || lower.includes('ira')) return CategoryIcons.retirement;
  if (lower.includes('property') || lower.includes('real estate')) return CategoryIcons.property;
  if (lower.includes('dividend')) return CategoryIcons.dividend;
  return CategoryIcons.default;
}

// Confidence badge styles - matching Projections theme
const CONFIDENCE_BADGE = {
  high: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'High confidence' },
  medium: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Estimated' },
  estimated: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Approximate' },
} as const;

// Individual savings item
interface SavingsItemProps {
  item: TaxSavingsHighlight['savingsBreakdown'][0];
  maxAmount: number;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}

function SavingsItem({ item, maxAmount, index, isExpanded, onToggle }: SavingsItemProps) {
  const confidence = CONFIDENCE_BADGE[item.confidence ?? 'medium'] ?? CONFIDENCE_BADGE.medium;
  const percentage = maxAmount > 0 ? (item.amountINR / maxAmount) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.08 }}
      className={cn(
        'rounded-lg border bg-white transition-all cursor-pointer',
        isExpanded
          ? 'border-slate-300 shadow-sm'
          : 'border-slate-200 hover:border-slate-300'
      )}
      onClick={onToggle}
    >
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center text-slate-600">
              {getCategoryIcon(item.category)}
            </div>
            <div>
              <h4 className="font-medium text-slate-800 text-sm">{item.category}</h4>
              <p className="text-xs text-slate-500">{item.timing}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-base font-semibold text-slate-800">{item.formatted}</div>
            <span className={cn(
              'inline-flex items-center px-1.5 py-0.5 rounded text-xs',
              confidence.bg, confidence.text
            )}>
              {confidence.label}
            </span>
          </div>
        </div>

        {/* Progress bar - emerald accent for visual comparison */}
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-emerald-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 0.6, delay: 0.2 + index * 0.08, ease: 'easeOut' }}
          />
        </div>

        {/* Expanded details */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="pt-3 mt-3 border-t border-slate-100">
                <p className="text-sm text-slate-600">{item.description}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export function TaxSavingsBreakdown({ savings, className }: TaxSavingsBreakdownProps) {
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [showMethodology, setShowMethodology] = useState(false);

  const breakdownItems = savings?.savingsBreakdown ?? [];
  const maxAmount = breakdownItems.length > 0
    ? Math.max(...breakdownItems.map(item => item.amountINR ?? 0))
    : 1;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Tax Savings Breakdown</h3>
            <p className="text-sm text-slate-500">How your savings add up</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-emerald-600">{savings.totalFormatted}</div>
          <p className="text-xs text-slate-500">Total potential</p>
        </div>
      </div>

      {/* Savings Items */}
      <div className="space-y-2">
        {breakdownItems.map((item, index) => (
          <SavingsItem
            key={item.category}
            item={item}
            maxAmount={maxAmount}
            index={index}
            isExpanded={expandedItem === item.category}
            onToggle={() => setExpandedItem(
              expandedItem === item.category ? null : item.category
            )}
          />
        ))}
      </div>

      {/* Methodology */}
      <div className="rounded-lg bg-slate-50 border border-slate-200 overflow-hidden">
        <button
          onClick={() => setShowMethodology(!showMethodology)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium text-slate-700">How we calculated this</span>
          </div>
          <motion.span
            animate={{ rotate: showMethodology ? 180 : 0 }}
            className="text-slate-400"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </motion.span>
        </button>

        <AnimatePresence>
          {showMethodology && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-3 text-sm">
                <div className="p-3 bg-white rounded-md border border-slate-200">
                  <h5 className="font-medium text-slate-700 mb-1">Methodology</h5>
                  <p className="text-slate-600 text-sm">{savings.methodology}</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-md border border-amber-200">
                  <h5 className="font-medium text-amber-700 mb-1">Important Disclaimer</h5>
                  <p className="text-amber-700 text-xs">{savings.disclaimer}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default TaxSavingsBreakdown;
